import { Vue, Options } from 'vue-class-component';

import configurationsClient from '@/core/services/configurations-service';
import plantsClient from '@/core/services/plants';
import {
  DefaultRoomDTO,
  PlantEntityType,
} from '@/../client-generator/generated-clients/configurations';
import {
  IRoomDTO,
  RoomDTO,
  RoomType,
} from '@/../client-generator/generated-clients/plants';
import debounce from 'lodash.debounce';
import { FETCH_PLANT, HANDLE_REFRESH_PLANT } from '@/core/utils/emitter-events';
import { NamingService } from '@/core/services/naming.service';
import { NAME_VALIDATION_DEBOUNCE_TIME, getRoomIconFromType } from '@/core/utils/utils';

@Options({
  name: 'DialogConfigureRoom',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    plantId: {
      type: Number,
      requred: true,
    },
    roomId: {
      type: String,
      requred: true,
    },
    room: {
      type: Object as IRoomDTO,
      required: true,
    },
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
    },
    'theRoom.name': function () {
      this.verifyValidityOfName();
    },
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogConfigureRoom extends Vue {
  getRoomIcon = getRoomIconFromType
  isVisible!: boolean;
  isDialogVisible = false;
  isLoading = false;

  plantId!: number;
  roomId!: string;
  room!: IRoomDTO;
  theRoom: IRoomDTO | null = null;

  roomTypes: DefaultRoomDTO[] = [];

  /////////////////////////////////////////
  // Room naming validation properties
  /////////////////////////////////////////
  lockSave = true;
  isValidName = true;
  initialName!: string;
  invalidNameReason: string = '';

  mounted() {
    this.isDialogVisible = this.isVisible;
    this.fetchRoomTypes();

    this.theRoom = RoomDTO.fromJS(this.room);
    this.initialName = this.theRoom.name ?? '';
    this.verifyValidityOfName = debounce(
      this.verifyValidityOfName,
      NAME_VALIDATION_DEBOUNCE_TIME
    );
  }

  private async verifyValidityOfName() {
    this.lockSave = true;
    const validName = await NamingService.isValidEntityTypeName(
      this.theRoom?.name
    );
    if (this.initialName !== this.theRoom?.name && !validName) {
      let suggestedName = await NamingService.suggestNextValidEntityName({
        plantId: this.plantId,
        eType: PlantEntityType.ROOM,
        metadata: {
          roomType: this.theRoom?.type,
        },
      });
      suggestedName = suggestedName.trim();
      this.isValidName = validName;
      this.invalidNameReason = this.$t(
        `uniqueNaming.validationErrorWithSuggestion`,
        { suggestedName }
      );
      this.$emit('next-enabled', false);
    } else {
      this.lockSave = false;
      this.isValidName = true;
      this.invalidNameReason = '';
    }
  }

  async fetchRoomTypes() {
    this.isLoading = true;
    try {
      this.roomTypes = await configurationsClient.defaultRoomsList();
      this.roomTypes.forEach(r => r.label = this.$t(`AppResource.${RoomType[r.type!]}`))
    } catch (error) {
      console.log(error);
    } finally {
      this.isLoading = false;
    }
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  async doSubmit() {
    this.isLoading = true;

    const theRoom = new RoomDTO(this.theRoom as IRoomDTO);

    try {
      await plantsClient.roomsPUT(this.plantId, this.roomId, theRoom);
      this.$emitter.emit(FETCH_PLANT);
      this.handleHideDialog();
    } catch (error) {
      let messageError = '';

      switch (error.status) {
        case 409:
          this.$emitter.emit(HANDLE_REFRESH_PLANT);
          this.$router.push({
            name: 'page-plant-dashboard',
            params: this.$route.params,
          });
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      if (messageError) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 3000,
        });
      }
    } finally {
      this.isLoading = false;
    }
  }
}
